import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Outlet, useSearchParams } from 'react-router';

import { ONBOARDING_CONST } from '@/constant';
import { usePlan } from '@/hook';
import { VFlex, VStack, storage } from '@/package';
import { OnboardingProvider } from '@/provider/layout';
import type { OnboardingForm } from '@/type';

import vueXLogo from '/vue-x.png';

const OnboardingLayout = () => {
  const [title, setTitle] = useState<string | undefined>();
  const [layoutStyle, setLayoutStyle] = useState<
    React.CSSProperties | undefined
  >();
  const [_, rerender] = useState(0);
  const renderTitle = useRef<() => ReactNode>(null);
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get('accountId');
  const customerAWSAccountId = searchParams.get('customerAWSAccountId');
  const productCode = searchParams.get('productCode');
  const planList = usePlan();

  const setRenderTitle = (renderer?: () => ReactNode) => {
    setTitle(undefined);
    renderTitle.current = renderer ?? null;
    rerender(prev => prev + 1);
  };
  const layoutContextValue = useMemo(
    () => ({
      setLayoutStyle,
      setTitle: (title: string | undefined) => {
        setRenderTitle();
        setTitle(title);
      },
      setRenderTitle,
    }),
    [setLayoutStyle, setTitle, setRenderTitle],
  );

  const methods = useForm<OnboardingForm>({
    defaultValues: {
      email: '',
      inviteMembers: '',
      tenantName: '',
      tier: planList[0],
      enterpriseBillingUnit: 1,
      ...JSON.parse(
        storage.local.get(ONBOARDING_CONST.KEYS.STORAGE_ONBOARDING_FORM_DATA) ||
          '{}',
      ),
      personalInfoCollection: false,
      personalInfoTransfer: false,
      marketingPromotion: false,
      termsOfService: false,
      consentAITraining: false,
    },
  });

  useEffect(() => {
    const subscription = methods.watch(value => {
      storage.local.set(
        ONBOARDING_CONST.KEYS.STORAGE_ONBOARDING_FORM_DATA,
        JSON.stringify(value),
      );
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  useEffect(() => {
    methods.setValue(
      'productCode',
      productCode ? productCode : methods.watch('productCode'),
    );
    methods.setValue(
      'customerAWSAccountId',
      accountId ? accountId : methods.watch('customerAWSAccountId'),
    );
  }, [accountId, productCode, customerAWSAccountId]);

  return (
    <FormProvider {...methods}>
      <OnboardingProvider value={layoutContextValue}>
        <VFlex
          justify="center"
          className="w-full overflow-x-hidden overflow-y-auto"
          style={layoutStyle}
        >
          <VStack
            className="h-screen w-full overflow-y-auto py-30"
            items="center"
          >
            <div className="flex w-full flex-col items-center @md:max-w-[608px]">
              <img src={vueXLogo} className="w-18.5 lg:w-34" />

              <VFlex
                className="w-fit px-0.5 py-0.25 pt-6 text-2xl leading-[30px] text-black"
                items="center"
                justify="center"
              >
                {renderTitle.current ? renderTitle.current() : <h1>{title}</h1>}
              </VFlex>

              <Outlet />
            </div>
          </VStack>
        </VFlex>
      </OnboardingProvider>
    </FormProvider>
  );
};

export default OnboardingLayout;
